import { useState, useEffect, useCallback } from "react";

// ─── Broadcasts content height to parent window (for Squarespace iframe) ───
function useHeightBroadcaster() {
  useEffect(() => {
    function sendHeight() {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ type: "tb-tracker-height", height }, "*");
    }
    sendHeight();
    const observer = new MutationObserver(sendHeight);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    window.addEventListener("resize", sendHeight);
    const interval = setInterval(sendHeight, 1000);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", sendHeight);
      clearInterval(interval);
    };
  }, []);
}

// ═══════════════════════════════════════════════════════════
// PASTE YOUR APPS SCRIPT URL BELOW (between the quotes)
// ═══════════════════════════════════════════════════════════
const API_URL = "https://script.google.com/macros/s/AKfycbxagcIqPQu6FXDsj8p7SoiHbTk4-OmfmCnInfy2GSLQwdQ7ZOhiQ5CxTgBS75P90_pCjA/exec";
// ═══════════════════════════════════════════════════════════

const STAGES = [
  { id: "dugout", label: "Dugout", num: 1, desc: "You've been spotted in the footage" },
  { id: "ondeck", label: "On Deck", num: 2, desc: "Jordan & crew are cutting it together" },
  { id: "atbat", label: "At Bat", num: 3, desc: "Final eyes before it drops" },
  { id: "rounding", label: "Rounding Bases", num: 4, desc: "Locked in — drop date set" },
  { id: "homerun", label: "Home Run!", num: 5, desc: "You're famous. Go watch it!" },
];

const ORANGE = "#E8812B";
const ORANGE_LIGHT = "#F5A623";
const GREEN_DARK = "#1B3A1B";

// Strip time from date strings, keep just the date portion
function cleanDate(dateStr) {
  if (!dateStr) return "";
  // If it's already short like "4/9/2026", return as is
  // If it has time info, strip it
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }
  return dateStr;
}

// ─── API helpers ───
async function apiGet(action, params = {}) {
  const query = new URLSearchParams({ action, ...params }).toString();
  const res = await fetch(`${API_URL}?${query}`);
  return res.json();
}

async function apiPost(body) {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "text/plain" },
    body: JSON.stringify(body),
  });
  return res.json();
}

async function loadData() {
  try {
    const data = await apiGet("getAll");
    return { videos: data.videos || [], players: data.players || {} };
  } catch (e) {
    console.error("Failed to load:", e);
    return { videos: [], players: {} };
  }
}

/* ─── Stage badge ─── */
function StageBadge({ stage }) {
  const s = STAGES.find((st) => st.id === stage);
  if (!s) return null;
  const isLive = stage === "homerun";
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 12px", borderRadius: 20,
      background: isLive ? `${ORANGE}22` : "rgba(255,255,255,0.04)",
      border: `1px solid ${isLive ? `${ORANGE}44` : "rgba(255,255,255,0.08)"}`,
      fontSize: 12, color: isLive ? ORANGE : "rgba(255,255,255,0.55)",
      fontFamily: "'Bangers', cursive", letterSpacing: 1,
    }}>
      <span style={{ fontSize: 10 }}>⚾</span> {s.label}
    </div>
  );
}

/* ─── Mini progress dots ─── */
function ProgressDots({ stage }) {
  const idx = STAGES.findIndex((s) => s.id === stage);
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      {STAGES.map((s, i) => (
        <div key={s.id} style={{
          width: i <= idx ? 8 : 6,
          height: i <= idx ? 8 : 6,
          borderRadius: "50%",
          background: i <= idx ? (i === idx ? ORANGE : `${ORANGE}88`) : "rgba(255,255,255,0.1)",
          transition: "all 0.3s ease",
          boxShadow: i === idx ? `0 0 6px ${ORANGE}66` : "none",
        }} />
      ))}
    </div>
  );
}

/* ─── Latest Videos Feed ─── */
function LatestVideosFeed({ videos, players, onSelectVideo }) {
  const sorted = [...videos].reverse();
  const inProgress = sorted.filter((v) => v.stage !== "homerun");
  const completed = sorted.filter((v) => v.stage === "homerun");
  const display = [...inProgress, ...completed].slice(0, 6);

  if (display.length === 0) return null;

  const getPlayersForVideo = (videoId) => {
    const result = [];
    Object.entries(players).forEach(([username, vids]) => {
      if (vids.includes(videoId)) result.push(username);
    });
    return result;
  };

  return (
    <div style={{ animation: "tb-fade-up 0.8s ease", animationDelay: "0.2s", animationFillMode: "both" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        marginBottom: 16,
      }}>
        <div style={{
          fontFamily: "'Bangers', cursive", fontSize: "clamp(18px, 3.5vw, 22px)",
          color: "#fff", letterSpacing: 2,
        }}>
          <span style={{ color: ORANGE }}>🔥</span> What We're Working On
        </div>
        <div style={{
          flex: 1, height: 1,
          background: "linear-gradient(90deg, rgba(255,255,255,0.1), transparent)",
        }} />
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(clamp(240px, 40vw, 300px), 1fr))",
        gap: 12,
      }}>
        {display.map((video) => {
          const stageInfo = STAGES.find((s) => s.id === video.stage);
          const videoPlayers = getPlayersForVideo(video.id);
          const isLive = video.stage === "homerun";

          return (
            <div
              key={video.id}
              onClick={() => onSelectVideo(video)}
              style={{
                background: isLive
                  ? `linear-gradient(135deg, ${ORANGE}10, rgba(255,255,255,0.03))`
                  : "rgba(255,255,255,0.025)",
                border: `1px solid ${isLive ? `${ORANGE}25` : "rgba(255,255,255,0.06)"}`,
                borderRadius: 14,
                padding: "16px 18px",
                transition: "transform 0.2s ease, border-color 0.2s ease",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.borderColor = `${ORANGE}44`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = isLive ? `${ORANGE}25` : "rgba(255,255,255,0.06)";
              }}
            >
              {/* Live indicator */}
              {isLive && (
                <div style={{
                  position: "absolute", top: 12, right: 14,
                  display: "flex", alignItems: "center", gap: 5,
                  fontSize: 10, color: ORANGE, fontWeight: 700,
                  fontFamily: "'Bangers', cursive", letterSpacing: 1.5,
                }}>
                  <div style={{
                    width: 6, height: 6, borderRadius: "50%", background: ORANGE,
                    animation: "tb-pulse 2s ease-in-out infinite",
                  }} />
                  LIVE
                </div>
              )}

              {/* Title */}
              <div style={{
                fontWeight: 700, fontSize: "clamp(14px, 2.5vw, 16px)", color: "#fff",
                fontFamily: "'DM Sans', sans-serif",
                marginBottom: 4, paddingRight: isLive ? 50 : 0,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {video.title}
              </div>

              {/* Date */}
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 12 }}>
                {cleanDate(video.date)}
              </div>

              {/* Progress dots */}
              <div style={{ marginBottom: 10 }}>
                <ProgressDots stage={video.stage} />
              </div>

              {/* Stage + tap hint */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                flexWrap: "wrap", gap: 8,
              }}>
                <StageBadge stage={video.stage} />
                <div style={{
                  fontSize: 10, color: "rgba(255,255,255,0.2)",
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  Tap to view ›
                </div>
              </div>

              {/* Players */}
              {videoPlayers.length > 0 && (
                <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {videoPlayers.slice(0, 5).map((p) => (
                    <span key={p} style={{
                      padding: "2px 8px", borderRadius: 6,
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      fontSize: 11, color: "rgba(255,255,255,0.5)",
                      fontFamily: "'DM Sans', sans-serif",
                    }}>
                      {p}
                    </span>
                  ))}
                  {videoPlayers.length > 5 && (
                    <span style={{
                      padding: "2px 8px", borderRadius: 6,
                      background: `${ORANGE}15`,
                      border: `1px solid ${ORANGE}25`,
                      fontSize: 11, color: ORANGE,
                      fontFamily: "'DM Sans', sans-serif",
                    }}>
                      +{videoPlayers.length - 5} more
                    </span>
                  )}
                </div>
              )}

              {/* Stage description */}
              <div style={{
                fontSize: 11, color: "rgba(255,255,255,0.2)",
                marginTop: 8, fontStyle: "italic",
                fontFamily: "'DM Sans', sans-serif",
              }}>
                {stageInfo?.desc}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Selected Video Detail View ─── */
function VideoDetailView({ video, onBack }) {
  return (
    <div style={{ animation: "tb-fade-up 0.5s ease" }}>
      <button
        onClick={onBack}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "8px 16px", borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.1)",
          background: "rgba(255,255,255,0.03)",
          color: "rgba(255,255,255,0.6)", fontSize: 13,
          cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
          marginBottom: 16, transition: "border-color 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = `${ORANGE}44`)}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)")}
      >
        ← Back
      </button>
      <div style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 16,
        padding: "clamp(14px, 3vw, 20px) clamp(12px, 2.5vw, 16px)",
      }}>
        <DiamondTracker currentStage={video.stage} videoTitle={video.title} videoDate={cleanDate(video.date)} />
      </div>
    </div>
  );
}

/* ─── Baseball Diamond Tracker ─── */
function DiamondTracker({ currentStage, videoTitle, videoDate }) {
  const idx = STAGES.findIndex((s) => s.id === currentStage);
  const isHomeRun = idx === 4;
  const cx = 160, cy = 155, r = 100;

  const bases = [
    { x: cx, y: cy + r }, { x: cx + r, y: cy },
    { x: cx, y: cy - r }, { x: cx - r, y: cy },
    { x: cx, y: cy + r },
  ];

  const segments = [
    [bases[0], bases[1]], [bases[1], bases[2]],
    [bases[2], bases[3]], [bases[3], bases[4]],
  ];

  const labelOffsets = [
    { dx: 0, dy: 28, anchor: "middle" },
    { dx: 18, dy: 0, anchor: "start" },
    { dx: 0, dy: -18, anchor: "middle" },
    { dx: -18, dy: 0, anchor: "end" },
  ];

  const descOffsets = [
    { dx: 0, dy: 42 }, { dx: 18, dy: 14 },
    { dx: 0, dy: -6 }, { dx: -18, dy: 14 },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {videoTitle && (
        <div style={{ textAlign: "center", marginBottom: 10 }}>
          <div style={{ fontFamily: "'Bangers', cursive", fontSize: "clamp(18px, 4vw, 26px)", color: ORANGE, letterSpacing: 2 }}>{videoTitle}</div>
          {videoDate && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 4 }}>{videoDate}</div>}
        </div>
      )}

      <div style={{ width: "100%", maxWidth: 380, margin: "0 auto", padding: "0 8px" }}>
        <svg viewBox="0 0 320 310" style={{ width: "100%", height: "auto", overflow: "visible" }}>
          <defs>
            <linearGradient id={`grass-${videoTitle}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#2a5a2a" /><stop offset="100%" stopColor="#1a3a1a" />
            </linearGradient>
            <radialGradient id={`infield-${videoTitle}`} cx="50%" cy="50%" r="55%">
              <stop offset="0%" stopColor="#8B6914" /><stop offset="60%" stopColor="#6b4423" /><stop offset="100%" stopColor="#3a2510" />
            </radialGradient>
            <filter id={`glow-${videoTitle}`}><feGaussianBlur stdDeviation="3" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
            <filter id={`big-glow-${videoTitle}`}><feGaussianBlur stdDeviation="6" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
            <filter id={`runner-glow-${videoTitle}`}><feGaussianBlur stdDeviation="4" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          </defs>

          <rect x="0" y="0" width="320" height="310" rx="16" fill={`url(#grass-${videoTitle})`} />
          {Array.from({ length: 8 }).map((_, i) => (
            <line key={i} x1="0" y1={35 + i * 35} x2="320" y2={35 + i * 35} stroke="rgba(255,255,255,0.025)" strokeWidth="12" />
          ))}

          <polygon points={`${cx},${cy + r + 20} ${cx + r + 20},${cy} ${cx},${cy - r - 20} ${cx - r - 20},${cy}`} fill={`url(#infield-${videoTitle})`} opacity="0.6" />
          <circle cx={cx} cy={cy} r="8" fill="#6b4423" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
          <circle cx={cx} cy={cy} r="3" fill="#8B6914" />

          {segments.map((seg, i) => (
            <line key={`bg-${i}`} x1={seg[0].x} y1={seg[0].y} x2={seg[1].x} y2={seg[1].y} stroke="rgba(255,255,255,0.1)" strokeWidth="3" strokeLinecap="round" />
          ))}

          {segments.map((seg, i) => {
            if (i >= idx) return null;
            return (
              <g key={`lit-${i}`}>
                <line x1={seg[0].x} y1={seg[0].y} x2={seg[1].x} y2={seg[1].y} stroke={ORANGE} strokeWidth="6" strokeLinecap="round" opacity="0.3" filter={`url(#glow-${videoTitle})`} />
                <line x1={seg[0].x} y1={seg[0].y} x2={seg[1].x} y2={seg[1].y} stroke={i < 2 ? ORANGE : ORANGE_LIGHT} strokeWidth="3.5" strokeLinecap="round" />
              </g>
            );
          })}

          {idx > 0 && idx <= 4 && (
            <line x1={segments[idx - 1][0].x} y1={segments[idx - 1][0].y} x2={segments[idx - 1][1].x} y2={segments[idx - 1][1].y}
              stroke="#fff" strokeWidth="3.5" strokeLinecap="round" filter={`url(#glow-${videoTitle})`} />
          )}

          {bases.slice(0, 4).map((base, i) => {
            const reached = i <= idx || (i === 0 && isHomeRun);
            const active = i === idx || (i === 0 && isHomeRun);
            const size = i === 0 ? 10 : 9;
            if (i === 0) {
              const s = size;
              const pts = `${base.x},${base.y - s} ${base.x + s},${base.y - s / 3} ${base.x + s * 0.7},${base.y + s * 0.6} ${base.x - s * 0.7},${base.y + s * 0.6} ${base.x - s},${base.y - s / 3}`;
              return (
                <g key={`base-${i}`}>
                  {active && <polygon points={pts} fill="none" stroke={ORANGE} strokeWidth="2" opacity="0.5" filter={`url(#big-glow-${videoTitle})`} style={{ animation: "tb-pulse 2s ease-in-out infinite" }} />}
                  <polygon points={pts} fill={reached ? "#fff" : "rgba(255,255,255,0.15)"} stroke={reached ? (active ? ORANGE : "rgba(232,129,43,0.4)") : "rgba(255,255,255,0.08)"} strokeWidth="1.5" />
                </g>
              );
            }
            return (
              <g key={`base-${i}`}>
                {active && <rect x={base.x - size - 2} y={base.y - size - 2} width={(size + 2) * 2} height={(size + 2) * 2} transform={`rotate(45, ${base.x}, ${base.y})`} fill="none" stroke={ORANGE} strokeWidth="2" opacity="0.4" filter={`url(#big-glow-${videoTitle})`} style={{ animation: "tb-pulse 2s ease-in-out infinite" }} />}
                <rect x={base.x - size} y={base.y - size} width={size * 2} height={size * 2} transform={`rotate(45, ${base.x}, ${base.y})`} fill={reached ? (active ? "#fff" : "rgba(255,255,255,0.65)") : "rgba(255,255,255,0.12)"} stroke={reached ? (active ? ORANGE : "rgba(232,129,43,0.35)") : "rgba(255,255,255,0.06)"} strokeWidth="1.5" />
              </g>
            );
          })}

          <g filter={`url(#runner-glow-${videoTitle})`} style={{ animation: "tb-pulse 2s ease-in-out infinite" }}>
            <circle cx={bases[idx > 3 ? 0 : idx].x} cy={bases[idx > 3 ? 0 : idx].y} r={isHomeRun ? 14 : 11} fill="none" stroke={ORANGE} strokeWidth="2" opacity="0.5" />
            <circle cx={bases[idx > 3 ? 0 : idx].x} cy={bases[idx > 3 ? 0 : idx].y} r={isHomeRun ? 10 : 8} fill={ORANGE} />
            <text x={bases[idx > 3 ? 0 : idx].x} y={bases[idx > 3 ? 0 : idx].y + 1} textAnchor="middle" dominantBaseline="central" fontSize={isHomeRun ? "14" : "12"}>{isHomeRun ? "🏠" : "🏃"}</text>
          </g>

          {bases.slice(0, 4).map((base, i) => {
            const reached = i <= idx || (i === 0 && isHomeRun);
            const active = i === idx || (i === 0 && isHomeRun);
            const off = labelOffsets[i];
            const label = i === 0 && isHomeRun ? "HOME RUN!" : STAGES[i].label;
            return (
              <g key={`label-${i}`}>
                <text x={base.x + off.dx} y={base.y + off.dy} textAnchor={off.anchor} fill={active ? ORANGE : reached ? "rgba(255,255,255,0.65)" : "rgba(255,255,255,0.25)"} fontSize={active ? "13" : "10"} fontFamily="'Bangers', cursive" letterSpacing="1.5">{label.toUpperCase()}</text>
                {active && <text x={base.x + descOffsets[i].dx} y={base.y + descOffsets[i].dy + (i === 0 ? 14 : 0)} textAnchor={off.anchor} fill="rgba(232,129,43,0.6)" fontSize="7.5" fontFamily="'DM Sans', sans-serif">{STAGES[isHomeRun ? 4 : i].desc}</text>}
              </g>
            );
          })}

          {bases.slice(0, 4).map((base, i) => {
            if (i === 0 && !isHomeRun && idx === 0) return null;
            const reached = i < idx || (i === 0 && isHomeRun);
            if (!reached) return null;
            const bx = i === 0 ? base.x + 18 : i === 1 ? base.x + 16 : i === 2 ? base.x + 16 : base.x - 16;
            const by = i === 0 ? base.y - 14 : i === 1 ? base.y - 14 : i === 2 ? base.y + 14 : base.y - 14;
            return (
              <g key={`badge-${i}`}>
                <circle cx={bx} cy={by} r="7" fill={ORANGE} opacity="0.9" />
                <text x={bx} y={by + 1} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize="8" fontWeight="700">✓</text>
              </g>
            );
          })}
        </svg>
      </div>

      {isHomeRun && (
        <div style={{ textAlign: "center", marginTop: 4, fontFamily: "'Bangers', cursive", fontSize: "clamp(18px, 4vw, 24px)", color: ORANGE, letterSpacing: 3, animation: "tb-pulse 2s ease-in-out infinite" }}>
          ⚾ HOME RUN — YOU'RE LIVE! ⚾
        </div>
      )}
    </div>
  );
}

/* ─── Mini Diamond for Admin ─── */
function MiniDiamond({ stage }) {
  const idx = STAGES.findIndex((s) => s.id === stage);
  const cx = 25, cy = 25, r = 18;
  const bases = [{ x: cx, y: cy + r }, { x: cx + r, y: cy }, { x: cx, y: cy - r }, { x: cx - r, y: cy }, { x: cx, y: cy + r }];
  const segments = [[bases[0], bases[1]], [bases[1], bases[2]], [bases[2], bases[3]], [bases[3], bases[4]]];
  return (
    <svg viewBox="0 0 50 50" style={{ width: 42, height: 42, display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
      <rect x="0" y="0" width="50" height="50" rx="6" fill={GREEN_DARK} />
      {segments.map((seg, i) => (
        <line key={i} x1={seg[0].x} y1={seg[0].y} x2={seg[1].x} y2={seg[1].y}
          stroke={i < idx ? ORANGE : i === idx && idx > 0 ? "#fff" : "rgba(255,255,255,0.12)"}
          strokeWidth={i <= idx && (i < idx || idx > 0) ? "2" : "1"} strokeLinecap="round" />
      ))}
      {bases.slice(0, 4).map((b, i) => (
        <rect key={i} x={b.x - 2.5} y={b.y - 2.5} width="5" height="5" transform={`rotate(45, ${b.x}, ${b.y})`} fill={i <= idx ? "#fff" : "rgba(255,255,255,0.15)"} />
      ))}
      <text x={bases[idx > 3 ? 0 : idx].x} y={bases[idx > 3 ? 0 : idx].y + 1} textAnchor="middle" dominantBaseline="central" fontSize="7">🏃</text>
    </svg>
  );
}

/* ─── Player Search View ─── */
function PlayerView({ data, refreshData }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true); setSearched(true); setSelectedVideo(null);
    const freshData = await refreshData();
    const q = query.trim().toLowerCase();
    const playerVideos = [];
    Object.entries(freshData.players).forEach(([username, videoIds]) => {
      if (username.toLowerCase() === q) {
        videoIds.forEach((vid) => {
          const video = freshData.videos.find((v) => v.id === vid);
          if (video) playerVideos.push(video);
        });
      }
    });
    setResults(playerVideos);
    setLoading(false);
  };

  return (
    <div style={{ animation: "tb-fade-up 0.6s ease" }}>
      <div style={{ textAlign: "center", marginBottom: "clamp(24px, 5vw, 40px)" }}>
        <h1 style={{ fontFamily: "'Bangers', cursive", fontSize: "clamp(36px, 10vw, 72px)", margin: 0, letterSpacing: 4, lineHeight: 1.1 }}>
          <span style={{ color: "#fff" }}>VIDEO </span>
          <span style={{ color: ORANGE }}>TRACKER</span>
        </h1>
        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "clamp(13px, 2.5vw, 15px)", margin: "10px 0 0", fontFamily: "'DM Sans', sans-serif", padding: "0 16px" }}>
          Track your Derby Baseball video around the diamond
        </p>
      </div>

      <div style={{ display: "flex", gap: 8, maxWidth: 480, margin: "0 auto", padding: "0 16px" }}>
        <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} placeholder="Enter your username..."
          style={{ flex: 1, padding: "clamp(10px, 2vw, 14px) 14px", fontSize: "clamp(14px, 2.5vw, 16px)", border: "2px solid rgba(255,255,255,0.12)", borderRadius: 12, background: "rgba(255,255,255,0.06)", color: "#fff", outline: "none", fontFamily: "'DM Sans', sans-serif", transition: "border-color 0.3s", minWidth: 0 }}
          onFocus={(e) => (e.target.style.borderColor = ORANGE)}
          onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.12)")} />
        <button onClick={handleSearch} disabled={loading}
          style={{ padding: "clamp(10px, 2vw, 14px) clamp(16px, 3vw, 28px)", fontWeight: 700, border: "none", borderRadius: 12, background: loading ? "#555" : ORANGE, color: "#fff", cursor: loading ? "wait" : "pointer", fontFamily: "'Bangers', cursive", letterSpacing: 2, fontSize: "clamp(16px, 3vw, 20px)", transition: "transform 0.2s, box-shadow 0.2s", whiteSpace: "nowrap", flexShrink: 0 }}
          onMouseEnter={(e) => { if (!loading) { e.target.style.transform = "scale(1.05)"; e.target.style.boxShadow = "0 4px 20px rgba(232,129,43,0.4)"; } }}
          onMouseLeave={(e) => { e.target.style.transform = "scale(1)"; e.target.style.boxShadow = "none"; }}>
          {loading ? "..." : "TRACK"}
        </button>
      </div>

      {/* Search results */}
      {searched && results !== null && !loading && !selectedVideo && (
        <div style={{ marginTop: "clamp(24px, 5vw, 40px)", animation: "tb-fade-up 0.5s ease" }}>
          {results.length === 0 ? (
            <div style={{ textAlign: "center", padding: "clamp(24px, 5vw, 40px) 20px", background: "rgba(255,255,255,0.03)", borderRadius: 16, maxWidth: 480, margin: "0 auto" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
              <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "clamp(14px, 2.5vw, 16px)" }}>No videos found for <strong style={{ color: ORANGE }}>"{query}"</strong></div>
              <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 13, marginTop: 8 }}>Make sure you're using your exact in-game username</div>
            </div>
          ) : (
            <div style={{ maxWidth: 600, margin: "0 auto", padding: "0 12px" }}>
              <div style={{ textAlign: "center", fontFamily: "'Bangers', cursive", fontSize: "clamp(18px, 4vw, 24px)", letterSpacing: 2, marginBottom: 8 }}>
                <span style={{ color: "#fff" }}>🎯 Found {results.length} video{results.length > 1 ? "s" : ""} for </span>
                <span style={{ color: ORANGE }}>{query}</span>
              </div>
              {results.map((video) => (
                <div key={video.id} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: "clamp(14px, 3vw, 20px) clamp(12px, 2.5vw, 16px)", marginTop: 16 }}>
                  <DiamondTracker currentStage={video.stage} videoTitle={video.title} videoDate={cleanDate(video.date)} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Selected video from feed */}
      {selectedVideo && (
        <div style={{ marginTop: "clamp(24px, 5vw, 40px)", maxWidth: 600, marginLeft: "auto", marginRight: "auto", padding: "0 12px" }}>
          <VideoDetailView video={selectedVideo} onBack={() => setSelectedVideo(null)} />
        </div>
      )}

      {/* Latest Videos Feed */}
      {!selectedVideo && (
        <div style={{
          marginTop: searched ? "clamp(32px, 5vw, 48px)" : "clamp(36px, 6vw, 56px)",
          padding: "0 4px",
          maxWidth: 640,
          marginLeft: "auto",
          marginRight: "auto",
        }}>
          {searched && results && results.length > 0 && (
            <div style={{
              height: 1,
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)",
              marginBottom: "clamp(24px, 4vw, 36px)",
            }} />
          )}
          <LatestVideosFeed videos={data.videos} players={data.players} onSelectVideo={setSelectedVideo} />
        </div>
      )}
    </div>
  );
}

/* ─── Admin Panel ─── */
function AdminPanel({ data, setData, refreshData }) {
  const [newVideoTitle, setNewVideoTitle] = useState("");
  const [newVideoPlayers, setNewVideoPlayers] = useState("");
  const [addPlayerTo, setAddPlayerTo] = useState(null);
  const [newPlayerForVideo, setNewPlayerForVideo] = useState("");
  const [notification, setNotification] = useState("");
  const [saving, setSaving] = useState(false);

  const notify = (msg) => { setNotification(msg); setTimeout(() => setNotification(""), 2500); };

  const inputStyle = {
    width: "100%", padding: "12px 14px", borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.05)",
    color: "#fff", fontSize: 14, boxSizing: "border-box", fontFamily: "'DM Sans', sans-serif",
  };

  const addVideo = async () => {
    if (!newVideoTitle.trim()) return;
    setSaving(true);
    const players = newVideoPlayers.split(",").map((p) => p.trim().toLowerCase()).filter(Boolean);
    try {
      await apiPost({ action: "addVideo", title: newVideoTitle.trim(), players });
      const fresh = await refreshData(); setData(fresh);
      notify(`✅ Added "${newVideoTitle.trim()}" with ${players.length} player(s)`);
      setNewVideoTitle(""); setNewVideoPlayers("");
    } catch (e) { notify("❌ Failed to add video"); }
    setSaving(false);
  };

  const updateStage = async (videoId, newStage) => {
    setSaving(true);
    try {
      await apiPost({ action: "updateStage", videoId, stage: newStage });
      const fresh = await refreshData(); setData(fresh); notify("✅ Status updated");
    } catch (e) { notify("❌ Failed to update"); }
    setSaving(false);
  };

  const addPlayerToVideo = async (videoId) => {
    if (!newPlayerForVideo.trim()) return;
    setSaving(true);
    try {
      await apiPost({ action: "addPlayer", username: newPlayerForVideo.trim(), videoId });
      const fresh = await refreshData(); setData(fresh);
      setNewPlayerForVideo(""); setAddPlayerTo(null); notify(`✅ Added ${newPlayerForVideo.trim()}`);
    } catch (e) { notify("❌ Failed to add player"); }
    setSaving(false);
  };

  const removePlayer = async (username, videoId) => {
    setSaving(true);
    try {
      await apiPost({ action: "removePlayer", username, videoId });
      const fresh = await refreshData(); setData(fresh); notify(`Removed ${username}`);
    } catch (e) { notify("❌ Failed to remove"); }
    setSaving(false);
  };

  const deleteVideo = async (videoId) => {
    setSaving(true);
    try {
      await apiPost({ action: "deleteVideo", videoId });
      const fresh = await refreshData(); setData(fresh); notify("🗑️ Video deleted");
    } catch (e) { notify("❌ Failed to delete"); }
    setSaving(false);
  };

  const getPlayersForVideo = (videoId) =>
    Object.entries(data.players).filter(([, vids]) => vids.includes(videoId)).map(([u]) => u);

  return (
    <div style={{ animation: "tb-fade-up 0.6s ease" }}>
      <h2 style={{ fontFamily: "'Bangers', cursive", fontSize: "clamp(28px, 6vw, 36px)", letterSpacing: 3, textAlign: "center", margin: "0 0 6px" }}>
        <span style={{ color: "#fff" }}>ADMIN</span> <span style={{ color: ORANGE }}>PANEL</span>
      </h2>
      <p style={{ textAlign: "center", color: "rgba(255,255,255,0.35)", fontSize: 12, marginBottom: 24, fontFamily: "'DM Sans', sans-serif" }}>
        Manage videos and player assignments
      </p>

      {saving && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.9)", border: `1px solid ${ORANGE}44`, borderRadius: 10, padding: "10px 20px", color: "rgba(255,255,255,0.6)", fontSize: 13, zIndex: 999 }}>Saving...</div>
      )}
      {notification && (
        <div style={{ position: "fixed", top: 20, right: 20, background: "rgba(0,0,0,0.9)", border: `1px solid ${ORANGE}`, borderRadius: 10, padding: "12px 20px", color: "#fff", fontSize: 14, zIndex: 999, animation: "tb-fade-up 0.3s ease" }}>{notification}</div>
      )}

      <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "clamp(16px, 3vw, 24px)", marginBottom: 24 }}>
        <div style={{ fontFamily: "'Bangers', cursive", color: ORANGE, marginBottom: 14, letterSpacing: 2, fontSize: 20 }}>+ NEW VIDEO</div>
        <input type="text" placeholder="Video title (e.g. 'Derby Highlights Ep. 12')" value={newVideoTitle} onChange={(e) => setNewVideoTitle(e.target.value)} style={{ ...inputStyle, marginBottom: 10 }} />
        <input type="text" placeholder="Player usernames (comma separated)" value={newVideoPlayers} onChange={(e) => setNewVideoPlayers(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addVideo()} style={{ ...inputStyle, marginBottom: 14 }} />
        <button onClick={addVideo} disabled={saving} style={{ padding: "10px 24px", border: "none", borderRadius: 10, background: saving ? "#555" : ORANGE, color: "#fff", fontSize: 16, cursor: saving ? "wait" : "pointer", fontFamily: "'Bangers', cursive", letterSpacing: 2 }}>
          {saving ? "Saving..." : "Add Video"}
        </button>
      </div>

      {data.videos.length === 0 && <div style={{ textAlign: "center", color: "rgba(255,255,255,0.3)", padding: 40 }}>No videos yet. Add one above to get started.</div>}

      {[...data.videos].reverse().map((video) => {
        const players = getPlayersForVideo(video.id);
        const currentStage = STAGES.find((s) => s.id === video.stage);
        return (
          <div key={video.id} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "clamp(14px, 2.5vw, 20px)", marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <MiniDiamond stage={video.stage} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: "clamp(14px, 2.5vw, 16px)", color: "#fff", fontFamily: "'DM Sans', sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{video.title}</div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>{cleanDate(video.date)}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                <select value={video.stage} onChange={(e) => updateStage(video.id, e.target.value)} disabled={saving}
                  style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.06)", color: "#fff", fontSize: 13, cursor: "pointer" }}>
                  {STAGES.map((s) => (<option key={s.id} value={s.id} style={{ background: "#1a1a1a" }}>⚾ {s.label}</option>))}
                </select>
                <button onClick={() => deleteVideo(video.id)} disabled={saving} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${ORANGE}33`, background: `${ORANGE}15`, color: ORANGE, fontSize: 12, cursor: "pointer" }}>🗑️</button>
              </div>
            </div>

            <div style={{ display: "inline-block", marginTop: 10, padding: "4px 12px", borderRadius: 20, background: `${ORANGE}18`, border: `1px solid ${ORANGE}33`, fontSize: 12, color: ORANGE, fontFamily: "'Bangers', cursive", letterSpacing: 1 }}>
              ⚾ {currentStage?.label}
            </div>

            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 6, fontFamily: "'DM Sans', sans-serif" }}>PLAYERS ({players.length})</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {players.map((p) => (
                  <span key={p} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", fontSize: 13, color: "#fff" }}>
                    {p}<span onClick={() => removePlayer(p, video.id)} style={{ cursor: "pointer", opacity: 0.4, fontSize: 11 }}>✕</span>
                  </span>
                ))}
                {addPlayerTo === video.id ? (
                  <div style={{ display: "flex", gap: 4 }}>
                    <input type="text" value={newPlayerForVideo} onChange={(e) => setNewPlayerForVideo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addPlayerToVideo(video.id)} placeholder="username" autoFocus
                      style={{ padding: "4px 10px", borderRadius: 8, border: `1px solid ${ORANGE}55`, background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13, width: 120 }} />
                    <button onClick={() => addPlayerToVideo(video.id)} style={{ padding: "4px 10px", borderRadius: 8, border: "none", background: ORANGE, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Add</button>
                    <button onClick={() => { setAddPlayerTo(null); setNewPlayerForVideo(""); }} style={{ padding: "4px 8px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "transparent", color: "#999", fontSize: 12, cursor: "pointer" }}>✕</button>
                  </div>
                ) : (
                  <button onClick={() => setAddPlayerTo(video.id)} style={{ padding: "4px 10px", borderRadius: 8, border: "1px dashed rgba(255,255,255,0.15)", background: "transparent", color: "rgba(255,255,255,0.4)", fontSize: 13, cursor: "pointer" }}>+ add player</button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Main App ─── */
export default function VideoTracker() {
  const [view, setView] = useState("player");
  const [data, setData] = useState({ videos: [], players: {} });
  const [loading, setLoading] = useState(true);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const refreshData = useCallback(async () => {
    const fresh = await loadData(); setData(fresh); return fresh;
  }, []);

  useHeightBroadcaster();

  useEffect(() => { refreshData().then(() => setLoading(false)); }, [refreshData]);

  const handleAdminLogin = async () => {
    if (!pin.trim()) return;
    setVerifying(true);
    try {
      const result = await apiPost({ action: "verifyPin", pin });
      if (result.success) {
        setView("admin");
        setPinError(false);
      } else {
        setPinError(true);
      }
    } catch (e) {
      setPinError(true);
    }
    setVerifying(false);
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#111", color: "#fff", flexDirection: "column", gap: 12 }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Bangers&display=swap');
          @keyframes tb-pulse { 0%, 100% { transform: scale(1); filter: brightness(1); } 50% { transform: scale(1.05); filter: brightness(1.15); } }
        `}</style>
        <div style={{ fontFamily: "'Bangers', cursive", fontSize: 28, letterSpacing: 4, color: ORANGE, animation: "tb-pulse 1.5s ease-in-out infinite" }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg, #111 0%, #0a0a0a 40%, #111 100%)", color: "#fff", fontFamily: "'DM Sans', 'Segoe UI', sans-serif", position: "relative", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bangers&family=DM+Sans:wght@400;500;700&display=swap');
        @keyframes tb-pulse { 0%, 100% { transform: scale(1); filter: brightness(1); } 50% { transform: scale(1.05); filter: brightness(1.15); } }
        @keyframes tb-fade-up { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes tb-float { from { transform: translateY(0) translateX(0); } to { transform: translateY(-30px) translateX(15px); } }
        * { box-sizing: border-box; }
        input::placeholder { color: rgba(255,255,255,0.25); }
        select option { background: #1a1a1a; }
        .tb-desktop-nav { display: flex; }
        .tb-mobile-toggle { display: none; }
        @media (max-width: 640px) {
          .tb-desktop-nav { display: none !important; }
          .tb-mobile-toggle { display: flex !important; }
        }
      `}</style>

      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} style={{
            position: "absolute", width: `${3 + Math.random() * 3}px`, height: `${3 + Math.random() * 3}px`,
            borderRadius: "50%", background: i % 2 === 0 ? `${ORANGE}40` : "rgba(255,255,255,0.1)",
            left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
            animation: `tb-float ${6 + Math.random() * 8}s ease-in-out infinite alternate`,
            animationDelay: `${Math.random() * -10}s`, opacity: 0.4,
          }} />
        ))}
      </div>

      {/* Header */}
      <div style={{
        position: "relative", zIndex: 20,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "clamp(14px, 2.5vw, 20px) clamp(16px, 3vw, 32px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>
        {/* Desktop nav */}
        <nav className="tb-desktop-nav" style={{ alignItems: "center", gap: "clamp(16px, 3vw, 28px)" }}>
          <a href="https://throwbackvr.com/team" style={{ color: "rgba(255,255,255,0.7)", textDecoration: "none", fontSize: 14, fontFamily: "'DM Sans', sans-serif", transition: "color 0.2s" }}
            onMouseEnter={(e) => (e.target.style.color = "#fff")}
            onMouseLeave={(e) => (e.target.style.color = "rgba(255,255,255,0.7)")}>Team</a>
          <a href="https://www.linkedin.com/company/throwbackvr" style={{ color: "rgba(255,255,255,0.7)", textDecoration: "none", fontSize: 14, fontFamily: "'DM Sans', sans-serif", transition: "color 0.2s" }}
            onMouseEnter={(e) => (e.target.style.color = "#fff")}
            onMouseLeave={(e) => (e.target.style.color = "rgba(255,255,255,0.7)")}>Careers</a>
          <a href="https://video-tracker-kappa.vercel.app" style={{ color: ORANGE, textDecoration: "none", fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>Video Tracker</a>
        </nav>

        {/* Mobile hamburger */}
        <div
          className="tb-mobile-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          style={{ cursor: "pointer", flexDirection: "column", gap: 5, zIndex: 200, padding: 4 }}
        >
          <span style={{ width: 22, height: 2, background: "#fff", borderRadius: 1, display: "block" }} />
          <span style={{ width: 22, height: 2, background: "#fff", borderRadius: 1, display: "block" }} />
          <span style={{ width: 16, height: 2, background: "#fff", borderRadius: 1, display: "block" }} />
        </div>

        {/* Center logo */}
        <a href="https://throwbackvr.com" style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", textDecoration: "none" }}>
          <img
            src="https://images.squarespace-cdn.com/content/v1/654192ca54d8841d6422dc23/0b4b32d0-4c23-4936-b8c7-046decc3854a/Throwback+Studios+Full+Logo+Transparent+Perfect+White.png?format=300w"
            alt="Throwback Studios"
            style={{ height: "clamp(32px, 4vw, 48px)", objectFit: "contain" }}
          />
        </a>

        {/* Desktop admin button */}
        <div
          className="tb-desktop-nav"
          onClick={() => { if (view === "admin") { setView("player"); setPin(""); } else setView("admin-login"); }}
          style={{
            padding: "5px 12px", borderRadius: 6,
            border: "1px solid rgba(255,255,255,0.06)",
            background: view === "admin" ? `${ORANGE}18` : "transparent",
            color: view === "admin" ? ORANGE : "rgba(255,255,255,0.15)",
            fontSize: 10, cursor: "pointer", fontWeight: 600,
            fontFamily: "'DM Sans', sans-serif",
            transition: "color 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.4)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = view === "admin" ? ORANGE : "rgba(255,255,255,0.15)")}
        >
          {view === "admin" ? "← Player View" : "Admin"}
        </div>

        {/* Mobile spacer for layout balance */}
        <div className="tb-mobile-toggle" style={{ width: 30 }} />
      </div>

      {/* Mobile fullscreen menu */}
      {menuOpen && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.97)",
          zIndex: 150, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 32,
          animation: "tb-fade-up 0.2s ease",
        }}>
          <div
            onClick={() => setMenuOpen(false)}
            style={{ position: "absolute", top: 20, right: 20, color: "#fff", fontSize: 28, cursor: "pointer" }}
          >✕</div>
          <a href="https://throwbackvr.com/team" style={{ color: "#fff", fontSize: 22, textDecoration: "none", letterSpacing: 2, fontFamily: "'Bangers', cursive" }}>Team</a>
          <a href="https://www.linkedin.com/company/throwbackvr" style={{ color: "#fff", fontSize: 22, textDecoration: "none", letterSpacing: 2, fontFamily: "'Bangers', cursive" }}>Careers</a>
          <span onClick={() => { setView("player"); setMenuOpen(false); }} style={{ color: ORANGE, fontSize: 22, cursor: "pointer", letterSpacing: 2, fontFamily: "'Bangers', cursive", fontWeight: 700 }}>Video Tracker</span>
          <div style={{ width: 40, height: 1, background: "rgba(255,255,255,0.1)" }} />
          <span
            onClick={() => { if (view === "admin") { setView("player"); setPin(""); } else { setView("admin-login"); } setMenuOpen(false); }}
            style={{ color: "rgba(255,255,255,0.3)", fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
          >
            {view === "admin" ? "← Player View" : "Admin"}
          </span>
        </div>
      )}

      <div style={{ position: "relative", zIndex: 10, maxWidth: 720, margin: "0 auto", padding: "clamp(28px, 5vw, 48px) clamp(8px, 2vw, 16px) 60px" }}>
        {view === "player" && <PlayerView data={data} refreshData={refreshData} />}
        {view === "admin-login" && (
          <div style={{ textAlign: "center", animation: "tb-fade-up 0.5s ease", maxWidth: 340, margin: "clamp(40px, 8vw, 80px) auto", padding: "0 16px" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
            <div style={{ fontFamily: "'Bangers', cursive", fontSize: 28, letterSpacing: 3, marginBottom: 20 }}>
              <span style={{ color: "#fff" }}>ADMIN</span> <span style={{ color: ORANGE }}>ACCESS</span>
            </div>
            <input type="password" value={pin} onChange={(e) => { setPin(e.target.value); setPinError(false); }} onKeyDown={(e) => e.key === "Enter" && handleAdminLogin()} placeholder="Enter PIN"
              style={{ width: "100%", padding: "14px 18px", borderRadius: 12, border: `2px solid ${pinError ? "#e74c3c" : "rgba(255,255,255,0.1)"}`, background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 18, textAlign: "center", letterSpacing: 8, marginBottom: 14, fontFamily: "'DM Sans', sans-serif" }} />
            {pinError && <div style={{ color: "#e74c3c", fontSize: 13, marginBottom: 10 }}>Incorrect PIN</div>}
            <button onClick={handleAdminLogin} disabled={verifying} style={{ padding: "12px 36px", border: "none", borderRadius: 12, background: verifying ? "#555" : ORANGE, color: "#fff", fontSize: 20, cursor: verifying ? "wait" : "pointer", fontFamily: "'Bangers', cursive", letterSpacing: 3 }}>
              {verifying ? "Verifying..." : "ENTER"}
            </button>
          </div>
        )}
        {view === "admin" && <AdminPanel data={data} setData={setData} refreshData={refreshData} />}
      </div>
    </div>
  );
}
